import useStagingAutoRefresh from './useStagingAutoRefresh';
import React, { useEffect, useMemo, useState } from 'react';
import './StagingClients.css';

const API = '/api/dashboards/admin/clients';

const statusName = value => ({
  active: 'Active',
  inactive: 'Deactivated',
  blocked: 'Deactivated',
  deleted: 'Removed'
}[String(value || '').toLowerCase()] || 'Unknown');

const dateLabel = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric'
      });
};

const rawAmount = value =>
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? '—'
    : new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(Number(value));

const bookingLabel = value => ({
  upcoming: 'Upcoming',
  InProgress: 'In progress',
  finished: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded'
}[value] || value || 'Unknown');

const safeCsv = value => {
  let text = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(text)) text = "'" + text;
  return '"' + text.replace(/"/g, '""') + '"';
};

function getToken() {
  try {
    const admin = JSON.parse(localStorage.getItem('admin') || 'null');
    return admin?.accessToken || null;
  } catch {
    return null;
  }
}

export default function StagingClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activity, setActivity] = useState('all');
  const [status, setStatus] = useState('current');
  const [sort, setSort] = useState('joined-desc');
  const autoRefreshKey = useStagingAutoRefresh();
  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState('details');
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingStatus, setBookingStatus] = useState('all');
  const [noteDraft, setNoteDraft] = useState('');
  const [clientNotes, setClientNotes] = useState([]);
  const [refresh, setRefresh] = useState(0);

  const updateClientAccountStatus = async (nextStatus) => {
    if (!selected || !["active", "inactive"].includes(nextStatus)) return;

    const current = String(selected.accountStatus || "").toLowerCase();
    if (current === nextStatus) return;

    const action = nextStatus === "inactive" ? "Deactivate" : "Reactivate";

    if (!window.confirm(
      `${action} ${selected.name || "this client"}? ` +
      "Their booking and payment history will be preserved."
    )) return;

    try {
      const token = getToken();

      if (!token) {
        throw new Error("Your administrator session has expired.");
      }

      const response = await fetch(
        `${API}/${encodeURIComponent(selected.id)}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": token
          },
          body: JSON.stringify({ status: nextStatus })
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to update account.");
      }

      setSelected(previous => previous
        ? { ...previous, accountStatus: result.accountStatus }
        : previous
      );

      setClients(previous => previous.map(client =>
        String(client.id) === String(selected.id)
          ? { ...client, accountStatus: result.accountStatus }
          : client
      ));

      setRefresh(previous => previous + 1);

    } catch (error) {
      window.alert(error.message || "Unable to update account status.");
    }
  };



  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!clients.length) setLoading(true);
      setError('');

      try {
        const token = getToken();
        if (!token) {
          throw new Error('Sign in with an authorised staging administrator account.');
        }

        const results = [];
        let total = null;

        for (let page = 1; page <= 1000; page++) {
          const response = await fetch(
            `${API}?page=${page}&perPage=100`,
            {
              headers: {
                'x-access-token': token,
                Accept: 'application/json'
              },
              cache: 'no-store'
            }
          );

          const json = await response.json();

          if (!response.ok || !json.success || !Array.isArray(json.clients)) {
            throw new Error(json.message || 'Unable to load clients.');
          }

          if (total === null) total = Number(json.total);

          results.push(...json.clients);

          if (results.length >= total) break;

          if (!json.clients.length) {
            throw new Error('Client pagination returned incomplete data.');
          }
        }

        if (results.length !== total) {
          throw new Error('Client list changed during loading. Refresh to retry.');
        }

        if (!cancelled) {
          setClients(Array.from(
            new Map(results.map(c => [String(c.id), c])).values()
          ));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Unable to load clients.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [refresh, autoRefreshKey]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return clients.filter(c => {
      const state = statusName(c.accountStatus);
      const matchesSearch = [c.id, c.name, c.email]
        .some(v => String(v ?? '').toLowerCase().includes(term));

      const matchesActivity =
        activity === 'all' ||
        (activity === 'booked' && c.confirmedBookings > 0) ||
        (activity === 'none' && c.confirmedBookings === 0) ||
        (activity === 'repeat' && c.completedAppointments >= 2);

      const matchesStatus =
        status === 'any' ||
        (status === 'current' && state !== 'Removed') ||
        status === state;

      return matchesSearch && matchesStatus && matchesActivity;
    }).sort((a, b) => {
      if (sort === 'bookings-desc') {
        return (b.confirmedBookings || 0) - (a.confirmedBookings || 0);
      }
      if (sort === 'completed-desc') {
        return (b.completedAppointments || 0) - (a.completedAppointments || 0);
      }
      if (sort === 'paid-desc') {
        return (Number(b.totalPaidOnline) || 0) -
               (Number(a.totalPaidOnline) || 0);
      }
      if (sort === 'paid-asc') {
        return (Number(a.totalPaidOnline) || 0) -
               (Number(b.totalPaidOnline) || 0);
      }
      if (sort === 'joined-desc') {
        return String(b.joined || '').localeCompare(String(a.joined || ''));
      }
      if (sort === 'joined-asc') {
        return String(a.joined || '').localeCompare(String(b.joined || ''));
      }
      return String(a.name || '').localeCompare(String(b.name || ''), 'en-GB');
    });
  }, [clients, search, activity, status, sort]);

  const reset = () => {
    setSearch('');
    setActivity('all');
    setStatus('current');
    setSort('joined-desc');
  };

  const exportList = () => {
    const rows = [
      ['Client ID', 'Name', 'Email', 'Join date', 'Account status'],
      ...filtered.map(c => [
        c.id, c.name, c.email, c.joined, statusName(c.accountStatus)
      ])
    ];

    const csv = rows.map(row => row.map(safeCsv).join(',')).join('\r\n');
    const url = URL.createObjectURL(
      new Blob([csv], { type: 'text/csv;charset=utf-8' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Beauty_Spot_Staging_Clients.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const openClient = client => {
    setSelected(client);
    setDetailTab('details');
    setBookingSearch('');
    setBookingStatus('all');
    setNoteDraft('');
    try {
      const saved = JSON.parse(
        localStorage.getItem('beautyspot-staging-client-notes-' + client.id) || '[]'
      );
      setClientNotes(Array.isArray(saved) ? saved : []);
    } catch {
      setClientNotes([]);
    }
  };

  const saveNote = () => {
    if (!selected || !noteDraft.trim()) return;
    const updated = [
      { id: Date.now(), text: noteDraft.trim(),
        created: new Date().toISOString() },
      ...clientNotes
    ];
    localStorage.setItem(
      'beautyspot-staging-client-notes-' + selected.id,
      JSON.stringify(updated)
    );
    setClientNotes(updated);
    setNoteDraft('');
  };

  return (
    <div className="bsc-page">
      <div className="bsc-heading">
        <h1>Clients</h1>
        <button
          type="button"
          className="bsc-export"
          onClick={exportList}
          disabled={loading || !!error}
        >
          Export filtered list ↗
        </button>
      </div>

      <div className="bsc-scope">
        <span>
          <strong>All sign-up dates</strong>
          {' · Current client status · Staging data'}
        </span>
        <button type="button" onClick={() => setRefresh(n => n + 1)}>
          Refresh
        </button>
      </div>

      <section className="bsc-panel">
        <div className="bsc-filters">
          <label className="bsc-search">
            Search clients
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Name, email or client ID"
            />
          </label>

          <label>
            Booking activity
            <select
              value={activity}
              onChange={e => setActivity(e.target.value)}
            >
              <option value="all">All clients</option>
              <option value="booked">Has confirmed bookings</option>
              <option value="none">No confirmed bookings</option>
              <option value="repeat">2+ completed appointments</option>
            </select>
          </label>

          <label>
            Account status
            <select value={status} onChange={e => setStatus(e.target.value)}>
              <option value="current">Current accounts</option>
              <option value="Active">Active</option>
              <option value="Deactivated">Deactivated</option>
              <option value="Removed">Removed · history</option>
              <option value="any">All statuses</option>
            </select>
          </label>

          <button type="button" className="bsc-reset" onClick={reset}>
            Reset filters
          </button>
        </div>

        <div className="bsc-toolbar">
          <span>{loading ? 'Loading clients…' : `${filtered.length} clients shown`}</span>
          <label>
            Sort by
            <select value={sort} onChange={e => setSort(e.target.value)}>
              <option value="bookings-desc">Most confirmed bookings</option>
              <option value="completed-desc">Most completed appointments</option>
              <option value="paid-desc">Highest total paid</option>
              <option value="paid-asc">Lowest total paid</option>
              <option value="joined-desc">Newest clients</option>
              <option value="joined-asc">Oldest clients</option>
              <option value="name-asc">Name A–Z</option>
            </select>
          </label>
        </div>

        <div className="bsc-note">
          Booking figures use staging records. Payment amounts await currency-unit verification.
        </div>

        {error && <p className="bsc-error" role="alert">{error}</p>}

        <div className="bsc-table-wrap">
          <table className="bsc-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Bookings</th>
                <th>Total paid</th>
                <th>Join date</th>
                <th>Account status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td>
                    <button
                      className="bsc-client-name"
                      type="button"
                      onClick={() => openClient(c)}
                    >
                      {c.name || c.email || `Client ${c.id}`}
                    </button>
                    <small>{c.email || 'Email not provided'}</small>
                    <small>{c.id}</small>
                  </td>
                  <td>{c.confirmedBookings ?? '—'}<small>Completed: {c.completedAppointments ?? '—'}</small></td>
                  <td>{rawAmount(c.totalPaidOnline)}</td>
                  <td>{dateLabel(c.joined)}</td>
                  <td>
                    <span className={
                      'bsc-status ' +
                      (statusName(c.accountStatus) === 'Active' ? 'active' : '')
                    }>
                      {statusName(c.accountStatus)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!loading && !error && filtered.length === 0 && (
            <div className="bsc-empty">
              <strong>No clients match</strong>
              <p>Try a different search or account status filter.</p>
            </div>
          )}
        </div>
      </section>

      {selected && (
        <div className="bsc-overlay" onMouseDown={() => setSelected(null)}>
          <aside
            className="bsc-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={`Client ${selected.name || selected.id}`}
            onMouseDown={e => e.stopPropagation()}
          >
            <button
              className="bsc-close"
              type="button"
              onClick={() => setSelected(null)}
              aria-label="Close client details"
            >×</button>

            <div className="bsc-eyebrow">BEAUTY SPOT ADMIN</div>
            <h2>{selected.name || selected.email || `Client ${selected.id}`}</h2>

            <button
              className="bsc-back"
              type="button"
              onClick={() => setSelected(null)}
            >← Back to clients</button>

            <div className="bsc-detail-head">
              <span className="bsc-status">
                {statusName(selected.accountStatus)}
              </span>
              <p>{selected.email}</p>
              <small>{selected.id} · Joined {dateLabel(selected.joined)}</small>
            </div>

            <div className="bsc-metrics">
              <div>
                <small>Confirmed bookings</small>
                <strong>{selected.confirmedBookings ?? '—'}</strong>
              </div>
              <div>
                <small>Completed appointments</small>
                <strong>{selected.completedAppointments ?? '—'}</strong>
              </div>
              <div>
                <small>Total paid online</small>
                <strong>{rawAmount(selected.totalPaidOnline)}</strong>
              </div>
            </div>

            <div className="bsc-detail-tabs">
              {[
                ['details', 'Contact details'],
                ['bookings', 'Bookings'],
                ['payments', 'Payments & refunds'],
                ['support', 'Support'],
                ['notes', 'Internal notes']
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={detailTab === key ? 'selected' : ''}
                  onClick={() => setDetailTab(key)}
                >{label}</button>
              ))}
            </div>

            {detailTab === 'details' ? (
              <div className="bsc-detail-body">
                <h3>Contact details</h3>
                <div className="bsc-contact-grid">
                  {[
                    ['Name', selected.name || 'Not provided'],
                    ['Email', selected.email || 'Not provided'],
                    ['Phone', selected.phone || 'Not provided'],
                    ['Client ID', selected.id],
                    ['Join date', dateLabel(selected.joined)]
                  ].map(([label, value]) => (
                    <div className="bsc-contact-field" key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>

                <h3 className="bsc-section-heading">Account status</h3>
                <select
                  className="bsc-status-select"
                  value={String(selected.accountStatus || '').toLowerCase()}
                  onChange={e => updateClientAccountStatus(e.target.value)}
                  aria-label="Account status"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Deactivated</option>
                  <option value="remove" disabled>Remove account…</option>
                </select>

                <details className="bsc-account-history">
                  <summary>Account history · unavailable</summary>
                  <p>Account status changes are saved to the staging database.</p>
                </details>
              </div>
            ) : detailTab === 'bookings' ? (
              <div className="bsc-detail-body">
                <h3>Booking history</h3>
                <p className="bsc-muted">
                  Includes cancelled booking history. Cancelled bookings
                  are excluded from the confirmed total. Booking status
                  and payment status are separate.
                </p>

                <div className="bsc-history-filters">
                  <input
                    aria-label="Search booking, provider or service"
                    placeholder="Search booking, provider or service"
                    value={bookingSearch}
                    onChange={e => setBookingSearch(e.target.value)}
                  />
                  <select value={bookingStatus}
                    aria-label="Filter booking status"
                    onChange={e => setBookingStatus(e.target.value)}>
                    <option value="all">All statuses</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="InProgress">In progress</option>
                    <option value="finished">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>

                {(() => {
                  const term = bookingSearch.trim().toLowerCase();
                  const rows = (selected.bookings || []).filter(b =>
                    (bookingStatus === 'all' ||
                      b.status === bookingStatus) &&
                    [b.reference, b.provider, b.service]
                      .some(v => String(v || '').toLowerCase().includes(term))
                  );
                  return (
                    <>
                      <p className="bsc-history-count">
                        {rows.length} appointments shown
                      </p>
                      <div className="bsc-history-wrap">
                        <table className="bsc-table">
                          <thead><tr>
                            <th>Appointment</th>
                            <th>Provider &amp; service</th>
                            <th>Booking status</th>
                            <th>Payment</th>
                          </tr></thead>
                          <tbody>
                            {rows.map(b => (
                              <tr key={b.id}>
                                <td>
                                  <span className="bsc-ref">{b.reference}</span>
                                  <small>{dateLabel(b.date)} · {b.time || '—'}</small>
                                </td>
                                <td>
                                  {b.provider}
                                  <small>{b.service}</small>
                                </td>
                                <td>
                                  <span className={
                                    'bsc-booking-status ' +
                                    (b.status === 'finished' ? 'completed' :
                                     b.status === 'InProgress' ? 'progress' : '')
                                  }>
                                    {bookingLabel(b.status)}
                                  </span>
                                </td>
                                <td>
                                  {b.paymentStatus || '—'}
                                  <small>{b.paymentMode || '—'}</small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {!rows.length && (
                          <p className="bsc-empty">No matching bookings.</p>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : detailTab === 'payments' ? (
              <div className="bsc-detail-body">
                <h3>Payments &amp; refunds</h3>
                <div className="bsc-payment-summary">
                  <div>
                    <span>Total paid online</span>
                    <strong>{rawAmount(selected.totalPaidOnline)}</strong>
                  </div>
                  <div>
                    <span>Refunded</span>
                    <strong>{rawAmount(selected.refundedOnline)}</strong>
                  </div>
                </div>
                <p className="bsc-muted">
                  Totals show recorded successful online payments before refunds.
                  Refunds are reported separately.
                  Provider wallet credits and Beauty Spot fees are separate.
                </p>
                <div className="bsc-history-wrap">
                  <table className="bsc-table">
                    <thead><tr>
                      <th>Payment / booking</th>
                      <th>Provider</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr></thead>
                    <tbody>
                      {(selected.payments || []).map(t => (
                        <tr key={'p-' + t.id}>
                          <td>
                            {t.id}
                            <small>Booking {t.bookingId} · {dateLabel(t.date)}</small>
                          </td>
                          <td>{(selected.bookings || [])
                            .find(b => String(b.id) === String(t.bookingId))
                            ?.provider || '—'}</td>
                          <td>{rawAmount(t.amount)}</td>
                          <td><span className="bsc-booking-status completed">Paid</span></td>
                        </tr>
                      ))}
                      {(selected.refunds || []).map(t => (
                        <tr key={'r-' + t.id}>
                          <td>
                            {t.id}
                            <small>Booking {t.bookingId} · {dateLabel(t.date)}</small>
                          </td>
                          <td>{(selected.bookings || [])
                            .find(b => String(b.id) === String(t.bookingId))
                            ?.provider || '—'}</td>
                          <td>{rawAmount(t.amount)}</td>
                          <td><span className="bsc-booking-status">Refunded</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!selected.payments?.length &&
                   !selected.refunds?.length && (
                    <p className="bsc-empty">No transactions recorded.</p>
                  )}
                </div>
              </div>
            ) : detailTab === 'support' ? (
              <div className="bsc-detail-body">
                <h3>Support tickets</h3>
                <p className="bsc-muted">
                  Support requests associated with this client will appear here
                  when the support system is connected.
                </p>
                <div className="bsc-history-filters">
                  <input disabled placeholder="Search support tickets"
                    aria-label="Search support tickets" />
                  <select disabled aria-label="Support ticket status">
                    <option>All statuses</option>
                  </select>
                </div>
                <div className="bsc-history-wrap">
                  <table className="bsc-table">
                    <thead><tr>
                      <th>Ticket</th><th>Subject</th>
                      <th>Status</th><th>Updated</th>
                    </tr></thead>
                  </table>
                  <p className="bsc-empty">
                    Support ticket history is not yet connected.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bsc-detail-body">
                <h3>Internal notes</h3>
                <p className="bsc-muted">
                  Visible only in this browser on this device.
                  Notes are not shared with other administrators.
                </p>
                <label className="bsc-note-label" htmlFor="bsc-client-note">
                  Note
                </label>
                <textarea
                  id="bsc-client-note"
                  className="bsc-note-input"
                  placeholder="Add context or a follow-up..."
                  value={noteDraft}
                  onChange={e => setNoteDraft(e.target.value)}
                />
                <button type="button"
                  className="bsc-save-note"
                  disabled={!noteDraft.trim()}
                  onClick={saveNote}>
                  Save note
                </button>
                {clientNotes.map(note => (
                  <div className="bsc-saved-note" key={note.id}>
                    <small>{dateLabel(note.created)}</small>
                    <p>{note.text}</p>
                  </div>
                ))}
                <p className="bsc-note-disclaimer">
                  Browser-only prototype notes · No messages or
                  notifications are sent.
                </p>
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
